import { Icon, Link, Text } from '@ecoinvent/ui';
import { FiHelpCircle } from 'react-icons/fi';

import { appConfig } from 'utilities/env';

import { TourStep } from '../Tour/types';

export const TOUR_VERSION = appConfig.tourVersion;
export const TOUR_SYSTEM_MODEL = 'cutoff';
export const TOUR_DATASET_ID = appConfig.tourDatasetId;

export const LICENSED_TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    type: 'card',
    title: 'Let us show you around',
    content: (
      <Text>
        Get the most from 27,000+ life cycle inventory datasets. Discover how to work with your data, understand flows, and
        measure environmental impacts.
      </Text>
    ),
    buttonText: 'Start Exploring',
    path: 'documentation',
  },
  {
    id: 'system-model',
    type: 'coachmark',
    targetId: 'system-model-selector',
    placement: 'bottom',
    title: 'Switch Between Versions & System Models',
    content: (
      <Text>
        Different versions include both updated and newly added datasets. The number represents the{' '}
        <Link href="https://support.ecoinvent.org/releases-overview" isExternal>
          database releases
        </Link>{' '}
        while the{' '}
        <Link href="https://support.ecoinvent.org/system-models-1" isExternal>
          system model
        </Link>{' '}
        (cutoff, EN15804, etc.) defines calculation approaches. We recommend using the{' '}
        <Link href="https://support.ecoinvent.org/system-models#Allocation_classification" isExternal>
          cut-off system model
        </Link>{' '}
        when starting to work with LCA.
      </Text>
    ),
    path: 'documentation',
  },
  {
    id: 'dataset-tabs',
    type: 'coachmark',
    targetId: 'dataset-tabs',
    placement: 'bottom',
    title: "Discover What's Inside Your Dataset",
    content: (
      <Text>
        Navigate through the tabs to dive deeper into your data. Start with Documentation for an overview and key details.
        Explore how materials and energy move in and out of the process with{' '}
        <Link href="https://support.ecoinvent.org/upr-lci-lcia#UPR" isExternal>
          Exchanges
        </Link>
        . Use{' '}
        <Link href="https://support.ecoinvent.org/upr-lci-lcia#LCI" isExternal>
          LCI
        </Link>{' '}
        Results and{' '}
        <Link href="https://support.ecoinvent.org/impact-assessment" isExternal>
          Impact Assessment
        </Link>{' '}
        to measure environmental impact across various indicators.
      </Text>
    ),
    path: 'documentation',
  },
  {
    id: 'first-exchange',
    type: 'coachmark',
    targetId: 'first-exchange',
    placement: 'top',
    title: 'Understand Exchanges',
    content: (
      <Text>
        Exchanges represent the input and output amounts of materials, energy, or substances. They are classified as
        intermediate exchanges, which occur between activities, and elementary exchanges, which occur between an activity and
        the environment. By clicking any row, you'll see detailed information about each exchange.
      </Text>
    ),
    path: 'exchanges',
  },
  {
    id: 'lci-results',
    type: 'coachmark',
    targetId: 'lci-amount',
    placement: 'left',
    title: 'Reading LCI Results',
    content: (
      <Text>
        LCI Results form the foundation for impact assessment—they quantify every resource used and emission generated. Here,
        each row shows the exchange name (the specific resource or emission), its compartment (where it's released, such as air
        or water), and the amount. To find specific items, use the filters for exchange name or compartment. Values can be
        copied from any row for use in your analysis.
      </Text>
    ),
    path: 'lci',
  },
  {
    id: 'lcia-method',
    type: 'coachmark',
    targetId: 'lcia-method-selector',
    placement: 'bottom',
    title: 'Choose the Right LCIA Method',
    content: (
      <Text>
        Impact Assessment translates inventory data into measurable environmental impacts across categories like climate change,
        resource depletion, and ecosystem damage.{' '}
        <Link href="https://support.ecoinvent.org/impact-assessment" isExternal>
          Different LCIA methods
        </Link>{' '}
        (
        <Link href="https://support.ecoinvent.org/guidance-on-ipcc-methods" isExternal>
          IPCC
        </Link>
        ,{' '}
        <Link href="https://www.rivm.nl/en/life-cycle-assessment-lca/recipe" isExternal>
          ReCiPe
        </Link>
        , etc.) use different scientific models to calculate these impacts, which is why you may see varying results for the
        same category depending on the method used.
      </Text>
    ),
    path: 'impact_assessment',
  },
  {
    id: 'files',
    type: 'coachmark',
    targetId: 'resources-chevron',
    placement: 'right',
    title: 'More Resources',
    content: (
      <Text>
        Download database files, access version-specific supporting manuals, and find project documentation and reports. Use
        these resources for working with raw data or dive deeper into methodology details.
      </Text>
    ),
    path: 'impact_assessment',
  },
  {
    id: 'completion',
    type: 'card',
    title: "You're All Set",
    content: (
      <Text>
        Great job. Start exploring datasets and using all the powerful features. To learn more about specific topics, look for
        the <Icon as={FiHelpCircle} mx={1} verticalAlign="middle" /> icons throughout the platform.
      </Text>
    ),
    buttonText: 'Finish Tour',
    path: 'impact_assessment',
  },
];
